import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  CircularProgress,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { promoCodesService } from './promo-codes.service';
import { UpdatePromoCodeDto } from './promo-codes.dto';

import { useToast } from '../../contexts/ToastContext';
const EditPromoCode = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState<UpdatePromoCodeDto>({
    promoCode: '',
    description: '',
    discountType: 'percentage',
    discountValue: 0,
    minimumOrderAmount: 0,
    maximumDiscount: 0,
    usageLimit: 0,
    startDate: '',
    endDate: '',
    status: 1, // 1 = Active, 0 = Inactive
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const statusOptions = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
  ];

  const typeOptions = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'fixed', label: 'Fixed Amount' },
  ];

  // Set up logout callback and fetch data
  useEffect(() => {
    promoCodesService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);

        const promoCode = await promoCodesService.getPromoCode(
          token,
          parseInt(id)
        );

        setFormData({
          promoCode: promoCode.promoCode,
          description: promoCode.description || '',
          discountType: promoCode.discountType,
          discountValue: promoCode.discountValue,
          minimumOrderAmount: promoCode.minimumOrderAmount ?? 0,
          maximumDiscount: promoCode.maximumDiscount ?? 0,
          usageLimit: promoCode.usageLimit ?? 0,
          startDate: promoCode.startDate,
          endDate: promoCode.endDate,
          status: promoCode.status,
        });
      } catch (err) {
        if (err instanceof Error) {
          showError(err.message);
        } else {
          showError('Failed to fetch promo code data');
        }
        navigate('/promo-codes');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  const handleChange =
    (field: keyof UpdatePromoCodeDto) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let value: string | number | string[] = (event.target as HTMLInputElement)
        .value;

      if (
        field === 'discountValue' ||
        field === 'minimumOrderAmount' ||
        field === 'maximumDiscount' ||
        field === 'usageLimit' ||
        field === 'status'
      ) {
        value = value === '' ? 0 : Number(value);
      }

      setFormData({ ...formData, [field]: value });
      if (errors[field as string]) {
        setErrors({ ...errors, [field as string]: '' });
      }
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData.promoCode !== undefined && !formData.promoCode.trim()) {
      newErrors.promoCode = 'Promo code is required';
    }

    if (formData.discountValue !== undefined && formData.discountValue <= 0) {
      newErrors.discountValue = 'Valid discount value is required';
    }

    if (
      formData.discountType === 'percentage' &&
      formData.discountValue !== undefined &&
      formData.discountValue > 99
    ) {
      newErrors.discountValue = 'Percentage cannot exceed 99%';
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    if (formData.usageLimit !== undefined && formData.usageLimit < 0) {
      newErrors.usageLimit = 'Usage limit cannot be negative';
    }

    // These two fields had no validation at all before (Admin Medium
    // #11, AUDIT_FIX_LOG.md). 0 is intentionally valid for both (backend
    // treats 0 as "no minimum order" / "no discount cap" - see
    // promo-codes.service.ts) - only negative values are rejected.
    if (
      formData.minimumOrderAmount != null &&
      formData.minimumOrderAmount < 0
    ) {
      newErrors.minimumOrderAmount = 'Minimum order amount cannot be negative';
    }
    if (formData.maximumDiscount != null && formData.maximumDiscount < 0) {
      newErrors.maximumDiscount = 'Maximum discount cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !token || !id) return;

    setSaving(true);
    try {
      // Clean form data - ensure all numeric fields are proper numbers
      const cleanedData: UpdatePromoCodeDto = {
        promoCode: formData.promoCode?.trim(),
        description: formData.description?.trim() || undefined,
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue ?? 0),
        minimumOrderAmount: Number(formData.minimumOrderAmount ?? 0),
        maximumDiscount: Number(formData.maximumDiscount ?? 0),
        usageLimit: Number(formData.usageLimit ?? 0),
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
      };

      await promoCodesService.updatePromoCode(token, parseInt(id), cleanedData);

      showSuccess('Promo code updated successfully!');

      setTimeout(() => {
        navigate('/promo-codes');
      }, 1500);
    } catch (err) {
      console.error('Error updating promo code:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to update promo code'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !id) return;

    try {
      await promoCodesService.deletePromoCode(token, parseInt(id));
      showSuccess('Promo code deleted successfully!');
      setDeleteDialog(false);
      setTimeout(() => {
        navigate('/promo-codes');
      }, 1500);
    } catch (err) {
      console.error('Error deleting promo code:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to delete promo code'
      );
    }
  };

  const handleCancel = () => {
    navigate('/promo-codes');
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate('/promo-codes')}
                sx={{ mr: 2 }}
              >
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit Promo Code
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteDialog(true)}
            >
              Delete
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update promotional code details and settings
          </Typography>
        </Paper>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left Sidebar - Status */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Promo Code Settings
                  </Typography>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={String(formData.status)}
                      label="Status"
                      onChange={handleChange('status')}
                    >
                      {statusOptions.map(option => (
                        <MenuItem
                          key={String(option.value)}
                          value={String(option.value)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>

            {/* Main Form Content */}
            <Grid item xs={12} md={8}>
              {/* Basic Information */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Promo Code"
                        placeholder="e.g., WELCOME20"
                        value={formData.promoCode}
                        onChange={handleChange('promoCode')}
                        error={!!errors.promoCode}
                        helperText={errors.promoCode}
                        inputProps={{
                          style: { textTransform: 'uppercase' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Discount Type</InputLabel>
                        <Select
                          value={formData.discountType}
                          label="Discount Type"
                          onChange={handleChange('discountType')}
                          error={!!errors.discountType}
                        >
                          {typeOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        placeholder="Brief description of the promo code"
                        value={formData.description}
                        onChange={handleChange('description')}
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={
                          formData.discountType === 'percentage'
                            ? 'Discount Percentage'
                            : 'Discount Amount'
                        }
                        type="number"
                        value={formData.discountValue}
                        onChange={handleChange('discountValue')}
                        error={!!errors.discountValue}
                        helperText={errors.discountValue}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>
                              {formData.discountType === 'percentage'
                                ? '%'
                                : '$'}
                            </Typography>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Validity Period */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Validity Period
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={formData.startDate}
                        onChange={handleChange('startDate')}
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange('endDate')}
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Discount Rules */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Discount Rules
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Minimum Order Amount"
                        type="number"
                        value={formData.minimumOrderAmount}
                        onChange={handleChange('minimumOrderAmount')}
                        error={!!errors.minimumOrderAmount}
                        helperText={errors.minimumOrderAmount}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>$</Typography>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Maximum Discount"
                        type="number"
                        value={formData.maximumDiscount}
                        onChange={handleChange('maximumDiscount')}
                        error={!!errors.maximumDiscount}
                        helperText={errors.maximumDiscount}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>$</Typography>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Usage Limit"
                        type="number"
                        value={formData.usageLimit}
                        onChange={handleChange('usageLimit')}
                        error={!!errors.usageLimit}
                        helperText={
                          errors.usageLimit || 'Leave 0 for unlimited'
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saving}
                >
                  {saving ? 'Updating...' : 'Update Promo Code'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this promo code? This action
              cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default EditPromoCode;
